import React, { useState } from "react";
import { BarChart3, TrendingUp, ArrowUpDown, Search, Zap, ArrowUp, ArrowDown } from "lucide-react";
import DataPanel from "../DataPanel";
import EmptyState from "../EmptyState";
import WidgetExportBtn from "../../reports/WidgetExportBtn";
import { flattenKeywords, flattenPositionChanges, flattenPositionDistribution, flattenSerpFeatures } from "../../reports/exportDataUtils";

interface KeywordsTabContentProps {
  liveData?: any | null;
  activeSubNav?: string;
}

const KeywordsTabContent: React.FC<KeywordsTabContentProps> = ({ liveData, activeSubNav }) => {
  const [activeTab, setActiveTab] = useState("all");

  // ─── SUB-NAV: Discovery ───
  if (activeSubNav === "discovery") {
    const keywords: any[] = liveData?.keywords ?? [];
    const hasKeywords = keywords.length > 0;

    const bySource = (src: string) => keywords.filter((k: any) => k.source === src);
    const similar = bySource("similar");
    const related = bySource("related");
    const questions = keywords.filter((k: any) => k.source === "questions" || k.keyword?.includes("?") || k.intents?.includes("question"));
    const longtail = bySource("longtail");

    const filteredKeywords =
      activeTab === "all"
        ? keywords
        : activeTab === "similar"
          ? similar
          : activeTab === "related"
            ? related
            : activeTab === "questions"
              ? questions
              : activeTab === "longtail"
                ? longtail
                : keywords;

    return (
      <div className="space-y-6">
        <DataPanel title="Discovered Keywords" icon={<Zap className="w-4 h-4" />} actions={<WidgetExportBtn title="Discovered Keywords" data={flattenKeywords(keywords)} />}>
          {hasKeywords ? (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  { key: "all", label: `All (${keywords.length})` },
                  { key: "similar", label: `Similar (${similar.length})` },
                  { key: "related", label: `Related (${related.length})` },
                  { key: "questions", label: `Questions (${questions.length})` },
                  { key: "longtail", label: `Longtail (${longtail.length})` },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                    style={{
                      background: activeTab === tab.key ? "rgba(91,61,245,0.2)" : "rgba(255,255,255,0.05)",
                      border: `1px solid ${activeTab === tab.key ? "rgba(91,61,245,0.3)" : "rgba(139,123,247,0.15)"
                        }`,
                      color: activeTab === tab.key ? "#8B7BF7" : "#9AA0B3",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                      {["Keyword", "Volume", "Difficulty", "CPC", "Source"].map((h) => (
                        <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredKeywords.slice(0, 10).map((kw: any, i: number) => (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{kw.keyword}</td>
                        <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(kw.volume ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 px-2">
                          <div className="flex items-center gap-2">
                            <div className="w-12 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                              <div className="h-full rounded-full" style={{ width: `${kw.difficulty ?? 0}%`, background: (kw.difficulty ?? 0) > 60 ? "#F87171" : (kw.difficulty ?? 0) > 40 ? "#FF9A4A" : "#4ADE80" }} />
                            </div>
                            <span className="text-xs" style={{ color: "#9AA0B3" }}>{kw.difficulty ?? "—"}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>${(kw.cpc ?? 0).toFixed(2)}</td>
                        <td className="py-2.5 px-2">
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(91,61,245,0.15)", color: "#8B7BF7" }}>
                            {kw.source ?? "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
                Showing {Math.min(filteredKeywords.length, 10)} of <span style={{ color: "#FF9A4A" }}>{keywords.length}</span> discovered keywords
              </div>
            </>
          ) : (
            <EmptyState title="No keyword discoveries yet" description="Run a Keyword Discovery audit to find new keyword opportunities." icon={<Zap className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
          )}
        </DataPanel>

        <DataPanel title="Question Keywords" icon={<Search className="w-4 h-4" />} actions={<WidgetExportBtn title="Question Keywords" data={flattenKeywords(questions)} />}>
          {questions.length > 0 ? (
            <div className="space-y-2">
              {questions.slice(0, 10).map((q: any, i: number) => (
                <div key={i} className="flex items-center justify-between rounded-xl px-4 py-3"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                  <span className="text-sm" style={{ color: "#C4BDE0" }}>{q.keyword}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs" style={{ color: "#9AA0B3" }}>Vol: {(q.volume ?? 0).toLocaleString()}</span>
                    <span className="text-xs font-bold" style={{ color: "#4ADE80" }}>Diff: {q.difficulty ?? "—"}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No question keywords yet" description="Run a Keyword Discovery audit to find question-based keywords." />
          )}
        </DataPanel>
      </div>
    );
  }

  // ─── SUB-NAV: SERP ───
  if (activeSubNav === "serp") {
    const serpFeatures: any[] = liveData?.serpFeatures ?? [];
    const keywordMetrics: any[] = liveData?.keywordMetrics ?? [];
    const hasSerpData = serpFeatures.length > 0 || keywordMetrics.length > 0;

    return (
      <div className="space-y-6">
        <DataPanel title="SERP Feature Keywords" icon={<Search className="w-4 h-4" />} actions={<WidgetExportBtn title="SERP Feature Keywords" data={flattenSerpFeatures(serpFeatures)} />}>
          {serpFeatures.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Position", "Volume", "Traffic", "SERP Features"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {serpFeatures.slice(0, 15).map((sf: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{sf.keyword}</td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#E8E4FF" }}>#{sf.position ?? "—"}</td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{(sf.volume ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#FF9A4A" }}>{(sf.traffic ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2">
                        <div className="flex flex-wrap gap-1">
                          {(sf.serpFeatures ?? []).map((f: string, j: number) => (
                            <span key={j} className="text-[10px] px-1.5 py-0.5 rounded"
                              style={{ background: "rgba(91,61,245,0.15)", color: "#8B7BF7" }}>
                              {f}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No SERP feature data yet" description="Run a SERP Analysis audit to see which SERP features you appear in." icon={<Search className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
          )}
        </DataPanel>

        <DataPanel title="Keyword Metrics" icon={<BarChart3 className="w-4 h-4" />} actions={<WidgetExportBtn title="Keyword Metrics" data={keywordMetrics.map(km => ({ Keyword: km.keyword, Volume: km.volume, CPC: km.cpc, Difficulty: km.difficulty, Competition: km.competition }))} />}>
          {keywordMetrics.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Volume", "CPC", "Difficulty", "Competition"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keywordMetrics.slice(0, 15).map((km: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{km.keyword}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(km.volume ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>${(km.cpc ?? 0).toFixed(2)}</td>
                      <td className="py-2.5 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                            <div className="h-full rounded-full" style={{ width: `${km.difficulty ?? 0}%`, background: (km.difficulty ?? 0) > 60 ? "#F87171" : (km.difficulty ?? 0) > 40 ? "#FF9A4A" : "#4ADE80" }} />
                          </div>
                          <span className="text-xs" style={{ color: "#9AA0B3" }}>{km.difficulty ?? "—"}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{km.competition ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !hasSerpData ? (
            <EmptyState title="No keyword metrics yet" description="Run a SERP Analysis audit to see detailed keyword metrics." />
          ) : null}
        </DataPanel>
      </div>
    );
  }

  // ─── DEFAULT SUB-NAV: Rankings ───
  const keywords: any[] | null = liveData?.keywords?.length ? liveData.keywords : null;
  const positionChanges: any[] = liveData?.positionChanges ?? [];
  const positionDistribution: any[] = liveData?.positionDistribution ?? [];
  const hasAuditRun = liveData?.analysisType === "keyword-rankings";

  return (
    <div className="space-y-6">
      <DataPanel title="Keyword Rankings" icon={<BarChart3 className="w-4 h-4" />} actions={<WidgetExportBtn title="Keyword Rankings" data={flattenKeywords(keywords || [])} />}>
        {keywords ? (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {["All Keywords", "Improved", "Declined", "New"].map((label, i) => (
                <button key={label} className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: i === 0 ? "rgba(91,61,245,0.2)" : "rgba(255,255,255,0.05)",
                    border: `1px solid ${i === 0 ? "rgba(91,61,245,0.3)" : "rgba(139,123,247,0.15)"}`,
                    color: i === 0 ? "#8B7BF7" : "#9AA0B3",
                  }}>
                  {label}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Position", "Δ", "Volume", "Difficulty", "Score"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keywords.slice(0, 10).map((kw: any, i: number) => {
                    const change = kw.change ?? "";
                    const changeStr = typeof change === "number" ? (change > 0 ? `+${change}` : `${change}`) : String(change);
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{kw.keyword}</td>
                        <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>#{kw.pos ?? kw.position ?? "—"}</td>
                        <td className="py-2.5 px-2 font-medium" style={{ color: changeStr.startsWith("+") ? "#4ADE80" : changeStr.startsWith("-") ? "#F87171" : "#9AA0B3" }}>
                          {changeStr || "—"}
                        </td>
                        <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{(kw.searchVolume ?? kw.volume ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 px-2">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                              <div className="h-full rounded-full" style={{ width: `${kw.difficulty ?? 0}%`, background: (kw.difficulty ?? 0) > 60 ? "#F87171" : (kw.difficulty ?? 0) > 40 ? "#FF9A4A" : "#4ADE80" }} />
                            </div>
                            <span className="text-xs" style={{ color: "#9AA0B3" }}>{kw.difficulty ?? "—"}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-2"><span className="font-bold" style={{ color: "#FF9A4A" }}>{kw?.score?.toLocaleString() ?? kw.trafficPotential?.toLocaleString() ?? "—"}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
              Showing {Math.min(keywords.length, 10)} of <span style={{ color: "#FF9A4A" }}>{keywords.length}</span> keywords
            </div>
          </>
        ) : (
          <EmptyState title="No keyword data yet" description="Run a Keyword Rankings audit to see your keywords." />
        )}
      </DataPanel>

      <DataPanel title="Position Distribution" icon={<ArrowUpDown className="w-4 h-4" />} actions={<WidgetExportBtn title="Position Distribution" data={flattenPositionDistribution(positionDistribution || [])} />}>
        {positionDistribution.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Period", "Keywords", "Top 1-5", "Top 6-10", "Top 11-20", "Top 21-50", "Top 51-100"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {positionDistribution.slice(0, 10).map((p: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{p.month}/{p.year}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(p.keywordsCount ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#4ADE80" }}>{(p.top1_5 ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#8B7BF7" }}>{(p.top6_10 ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#ce680f" }}>{(p.top11_20 ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#3d75db" }}>{(p.top21_50 ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#1ed9d9" }}>{(p.top51_100 ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
              Showing {Math.min(positionDistribution.length, 10)} of <span style={{ color: "#FF9A4A" }}>  {positionDistribution.length}</span> months
            </div>
          </>
        ) : (
          <EmptyState title="No position data yet" description="Run a Keyword Rankings audit to see position distribution." />
        )}
      </DataPanel>

      <DataPanel title="Recent Position Changes" icon={<TrendingUp className="w-4 h-4" />} actions={<WidgetExportBtn title="Recent Position Changes" data={flattenPositionChanges(positionChanges || [])} />}>
        {positionChanges.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Prev Pos", "Current Pos", "Change"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }} >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {positionChanges.slice(0, 10).map((item: any, i: number) => {
                    const change = item.change;

                    return (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}> {item.keyword}
                        </td>
                        <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>
                          #{item.prevPosition ?? "—"}
                        </td>
                        <td className="py-2.5 px-2 font-bold" style={{ color: "#E8E4FF" }}>
                          #{item.position ?? "—"}
                        </td>
                        <td className="py-2.5 px-2 font-medium">
                          {change === null ? (
                            <span style={{ color: "#9AA0B3" }}>New</span>
                          ) : change > 0 ? (
                            <div className="flex items-center gap-1" style={{ color: "#4ADE80" }}>
                              <ArrowUp className="w-3.5 h-3.5" />
                              <span>{Math.abs(change)}</span>
                            </div>
                          ) : change < 0 ? (
                            <div className="flex items-center gap-1" style={{ color: "#F87171" }}>
                              <ArrowDown className="w-3.5 h-3.5" />
                              <span>{Math.abs(change)}</span>
                            </div>
                          ) : (
                            <span style={{ color: "#9AA0B3" }}>0</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Footer */}
            <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }} >
              Showing {Math.min(positionChanges.length, 10)} of{" "}
              <span style={{ color: "#FF9A4A" }}>  {positionChanges.length}</span>{" "} changes
            </div>
          </>
        ) : (hasAuditRun ? (
          <EmptyState
            title="No position changes detected"
            description="Your keyword rankings are stable — no position changes found."
          />
        ) : (
          <EmptyState
            title="No data available"
            description="Run a Keyword Rankings audit to see position changes."
          />
        )
        )}
      </DataPanel>
    </div>
  );
};

export default KeywordsTabContent;
